import type { ReactNode } from "react";

export interface EmptyStateProps {
  /** Optional decorative element above the title (icon, illustration). */
  visual?: ReactNode;
  /** Primary heading. */
  title: ReactNode;
  /** Optional subtitle / body copy. */
  description?: ReactNode;
  /** Optional call-to-action — typically a `<ButtonIcon>`. */
  action?: ReactNode;
  /** Layout variant. `inline` for in-list placeholders, `page` for full-screen states. */
  variant?: "inline" | "page";
  /** Extra class on the outer wrapper. */
  className?: string;
}

/**
 * Used for "nothing to show here" surfaces and full-page error /
 * not-found / forbidden screens. One shape — title, optional
 * description, optional visual, optional CTA — replaces the
 * ad-hoc empty-list / error markup that was sprinkled across the
 * feed, matches, notifications, search, and user-details surfaces.
 *
 * The full-page Error/NotFound/Forbidden pages under
 * `src/pages/Error/` can compose this primitive as the body of
 * their layout; the primitive itself stays layout-agnostic.
 *
 * @example
 * ```tsx
 * <EmptyState
 *   variant="page"
 *   title="Page not found"
 *   description="The page you're trying to reach isn't here."
 *   action={
 *     <ButtonIcon id="home" label="Back home" onClick={goHome} />
 *   }
 * />
 * ```
 */
export function EmptyState({
  visual,
  title,
  description,
  action,
  variant = "inline",
  className,
}: EmptyStateProps) {
  return (
    <div
      className={["ui-empty-state", variant, className ?? ""].join(" ").trim()}
      role={variant === "page" ? "alert" : undefined}
    >
      {visual != null && <div className="ui-empty-state-visual">{visual}</div>}
      <div className="ui-empty-state-title">{title}</div>
      {description != null && <p className="ui-empty-state-description">{description}</p>}
      {action != null && <div className="ui-empty-state-action">{action}</div>}
    </div>
  );
}
